import { Avatar, Stack, Typography } from "@mui/material"
import { KeyboardEvent } from "react"
import { useNavigate } from "react-router-dom"
import type { IProjectAuthorShort, IProjectMemberSourceTeam } from "../../api/types/projects"
import { routesProfile } from "../../consts"
import { StyledBadge } from "../ui-kit"
import { ProjectTeamRoleChip } from "./ProjectTeamRoleChip"
import { TeamSourceLabel } from "./TeamSourceLabel"
import {
  canNavigateToUserProfile,
  getUserInitialsFromDisplayName,
  resolveUserAvatarUrl,
  resolveUserDisplayName,
} from "../../utils/deletedUserDisplay"

type ProjectTeamMemberCardData = {
  user: IProjectAuthorShort
  team_role: 'author' | 'coauthor' | 'member'
  role_label?: string | null
  is_author?: boolean
  source_team?: IProjectMemberSourceTeam | null
}

type Props = {
  member: ProjectTeamMemberCardData
  entity?: 'project' | 'team'
}

export const ProjectTeamMemberCard = ({ member, entity = 'project' }: Props) => {
  const navigate = useNavigate()
  const slug = (member.user.slug ?? '').trim()
  const hasSlug = canNavigateToUserProfile(member.user)
  const title = resolveUserDisplayName(member.user)

  const navigateToProfile = () => {
    if (!hasSlug) return
    navigate(`/${routesProfile.USERS.path}/${slug}`)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    navigateToProfile()
  }

  return (
    <Stack
      direction="row"
      gap={1.25}
      alignItems="center"
      sx={{
        width: '100%',
        cursor: hasSlug ? 'pointer' : 'default',
      }}
      onClick={hasSlug ? navigateToProfile : undefined}
      onKeyDown={hasSlug ? onKeyDown : undefined}
      role={hasSlug ? 'button' : undefined}
      tabIndex={hasSlug ? 0 : undefined}
    >
      <StyledBadge
        size={10}
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        variant="dot"
        invisible={!member.user.is_online}
      >
        <Avatar
          src={resolveUserAvatarUrl(member.user)}
          alt={title}
          sx={{ width: 44, height: 44 }}
        >
          {getUserInitialsFromDisplayName(title)}
        </Avatar>
      </StyledBadge>
      <Stack gap={0.5} minWidth={0}>
        <ProjectTeamRoleChip
          teamRole={member.team_role}
          roleLabel={member.role_label}
          isAuthor={member.is_author}
          entity={entity}
        />
        {member.source_team && <TeamSourceLabel team={member.source_team} />}
        <Typography
          variant="black-12-light"
          sx={{ lineHeight: '16px', overflowWrap: 'anywhere' }}
        >
          {title}
        </Typography>
      </Stack>
    </Stack>
  )
}
